import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import useIdleLogout from "@/hooks/useIdleLogout";
import { toast } from "sonner";
import api from "@/lib/api";
import ContactSupportDialog from "@/components/ContactSupportDialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList,
  Heart,
  Table2,
  History,
  Sprout,
  Settings as SettingsIcon,
  ShieldCheck,
  LogOut,
  Sparkles,
  Eye,
  X,
  Building2,
  CalendarDays,
  LifeBuoy,
} from "lucide-react";

const NAV = [
  { to: "/dashboard/daily", label: "Daily Rounds", icon: ClipboardList, testid: "nav-daily" },
  { to: "/dashboard/teasing", label: "Weekly Teasing", icon: Heart, testid: "nav-teasing" },
  { to: "/dashboard/mares", label: "Master Mares", icon: Table2, testid: "nav-mares" },
  { to: "/dashboard/history", label: "Mare History", icon: History, testid: "nav-history" },
  { to: "/dashboard/foals", label: "Foal Registry", icon: Sprout, testid: "nav-foals" },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const VET_NAV = { to: "/dashboard/farms", label: "Client Farms", icon: Building2, testid: "nav-farms" };
const VET_SCHEDULE_NAV = { to: "/dashboard/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" };
const ADMIN_NAV = { to: "/dashboard/admin", label: "Admin Console", icon: ShieldCheck, testid: "nav-admin" };

function FarmSwitcher() {
  const { activeFarm, setActiveFarm } = useAuth();
  const navigate = useNavigate();
  const [farms, setFarms] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/vet/workspaces").then(({ data }) => {
      if (!alive) return;
      setFarms(data);
      if (activeFarm && !data.find((f) => f.id === activeFarm.id)) setActiveFarm(null);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const onChange = (val) => {
    if (val === "__add__") { navigate("/dashboard/farms"); return; }
    const f = farms.find((x) => x.id === val);
    if (f) setActiveFarm(f);
  };

  return (
    <div className="mb-1" data-testid="farm-switcher">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono mb-1.5">Working in</p>
      <Select value={activeFarm?.id || ""} onValueChange={onChange}>
        <SelectTrigger className="w-full" data-testid="farm-switcher-trigger">
          <SelectValue placeholder="Select a farm…" />
        </SelectTrigger>
        <SelectContent>
          {farms.map((f) => (
            <SelectItem key={f.id} value={f.id} data-testid={`farm-switch-${f.id}`}>
              {f.name}{f.linked ? " · linked" : ""}
            </SelectItem>
          ))}
          <SelectItem value="__add__" data-testid="farm-switch-add">+ Manage farms…</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function TrialBanner({ user }) {
  if (!user || user.role === "admin") return null;
  const left = user.trial_days_left ?? 0;
  const tone =
    left <= 7 ? "bg-destructive text-destructive-foreground" : "bg-secondary text-secondary-foreground";
  return (
    <div
      data-testid="trial-banner"
      className={`px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 ${tone}`}
    >
      <Sparkles className="h-4 w-4" />
      {left > 0
        ? `Free trial — ${left} day${left === 1 ? "" : "s"} remaining`
        : "Your free trial has ended"}
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout, actingAs, setActingAs, activeFarm, pendingInviteCount } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-logout after 60 minutes of inactivity.
  const handleIdle = useCallback(() => {
    logout();
    toast.info("You were signed out after 60 minutes of inactivity.");
    navigate("/login");
  }, [logout, navigate]);
  useIdleLogout(handleIdle, 60 * 60 * 1000);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const exitActingAs = () => {
    setActingAs(null);
    navigate("/dashboard/admin");
  };

  const farm = user?.farm_name || user?.name || user?.email;
  const isVet = user?.account_type === "vet";
  let navItems = isVet ? [VET_SCHEDULE_NAV, VET_NAV, ...NAV] : NAV;
  if (user?.role === "admin") navItems = [...navItems, ADMIN_NAV];
  const needsFarm = isVet && !activeFarm;
  // Where the user accepts pending invites: vets on Client Farms, farms in Settings.
  const inviteBadgeTarget = isVet ? "/dashboard/farms" : "/dashboard/settings";
  const showInviteBadge = (to) => to === inviteBadgeTarget && pendingInviteCount > 0;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-primary text-primary-foreground sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <img src="/equimare-logo.png" alt="EquiMare Pro" className="h-9 w-9 rounded-lg object-cover ring-1 ring-white/20" />
            <div>
              <p className="font-extrabold text-base leading-none tracking-tight text-white">EquiMare Pro</p>
            </div>
          </div>
        </div>
        {isVet && (
          <div className="px-3 pt-4">
            <FarmSwitcher />
          </div>
        )}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto equi-scroll">
          <p className="px-3 pb-2 text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold">Menu</p>
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `relative flex items-center px-3 py-2.5 rounded-md text-[13px] font-medium transition-colors ${
                  isActive ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r bg-white" />}
                  {n.label}
                  {showInviteBadge(n.to) && (
                    <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="nav-invite-badge">
                      {pendingInviteCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-white/10">
          <div className="px-3 py-2 mb-2 rounded-md bg-white/5">
            <p className="text-[10px] uppercase tracking-[0.14em] text-white/40 font-semibold">Signed in</p>
            <p className="text-xs font-medium truncate mt-0.5 text-white/90" title={farm}>{farm}</p>
          </div>
          <ContactSupportDialog
            trigger={
              <Button variant="ghost" size="sm" className="w-full justify-start gap-2 mb-1 h-8 text-white/70 hover:text-white hover:bg-white/10" data-testid="contact-support-btn">
                <LifeBuoy className="h-4 w-4" /> Contact Support
              </Button>
            }
          />
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 h-8 text-white/70 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Log out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
          <div className="flex items-center gap-2">
            <img src="/equimare-logo.png" alt="EquiMare Pro" className="h-8 w-8 rounded-lg object-cover ring-1 ring-white/20" />
            <span className="font-extrabold tracking-tight text-white">EquiMare Pro</span>
          </div>
          <div className="flex items-center gap-1">
            <ContactSupportDialog
              trigger={
                <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10" data-testid="contact-support-btn-mobile">
                  <LifeBuoy className="h-4 w-4" />
                </Button>
              }
            />
            <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10" onClick={handleLogout} data-testid="logout-button-mobile">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto equi-scroll border-b border-border bg-card">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `relative flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 ${
                  isActive ? "border-primary text-primary" : "border-transparent text-muted-foreground"
                }`
              }
            >
              {n.label}
              {showInviteBadge(n.to) && (
                <span className="absolute top-1.5 right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold" data-testid="nav-invite-badge-mobile">
                  {pendingInviteCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {isVet && (
          <div className="md:hidden px-4 py-2 border-b border-border bg-card">
            <FarmSwitcher />
          </div>
        )}

        <TrialBanner user={user} />

        {isVet && activeFarm && (
          <div className="px-4 py-1.5 text-xs font-medium flex items-center justify-center gap-2 bg-secondary text-secondary-foreground border-b border-border" data-testid="active-farm-banner">
            <Building2 className="h-3.5 w-3.5" /> Working in <strong>{activeFarm.name}</strong>
          </div>
        )}

        {actingAs && (
          <div
            data-testid="acting-as-banner"
            className="px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-3 bg-primary text-primary-foreground"
          >
            <Eye className="h-4 w-4" />
            <span>
              Viewing <strong>{actingAs.label}</strong> — read-only
            </span>
            <button
              onClick={exitActingAs}
              data-testid="exit-acting-as"
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary-foreground/15 hover:bg-primary-foreground/25 px-3 py-1 text-xs font-semibold transition-colors"
            >
              <X className="h-3.5 w-3.5" /> Exit
            </button>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          {needsFarm && location.pathname !== "/dashboard" && !location.pathname.startsWith("/dashboard/farms") && !location.pathname.startsWith("/dashboard/schedule") && !location.pathname.startsWith("/dashboard/settings") ? (
            <div className="max-w-md mx-auto text-center py-24" data-testid="select-farm-prompt">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Building2 className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-xl font-extrabold tracking-tight">Pick a farm to get started</h2>
              <p className="text-muted-foreground text-sm mt-2 mb-6">
                Your veterinarian account manages multiple client farms. Choose one to work in, or add your first farm.
              </p>
              <Button onClick={() => navigate("/dashboard/farms")} className="gap-2" data-testid="go-to-farms-btn">
                <Building2 className="h-4 w-4" /> Go to Client Farms
              </Button>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
